/**
 * Modal reutilizável de ajuda sobre formatação de variáveis
 * Documenta todos os formatos suportados pela sintaxe {{variavel||formato}}
 * 
 * RELEASE: 1.0.0 - 2026-04-01 - Componente inicial com tabela de formatos
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';

interface VariableFormatHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dados de cada formato suportado para exibição na tabela
 */
const FORMAT_EXAMPLES = [
  // Datas
  { category: 'Data', format: 'dd/MM/yyyy', syntax: '{{campo||dd/MM/yyyy}}', input: '2026-03-27', output: '27/03/2026' },
  { category: 'Data', format: 'dd/MM/yyyy HH:mm', syntax: '{{campo||dd/MM/yyyy HH:mm}}', input: '2026-03-27T14:30:00', output: '27/03/2026 14:30' },
  { category: 'Data', format: "dd 'de' MMMM 'de' yyyy", syntax: "{{campo||dd 'de' MMMM 'de' yyyy}}", input: '2026-03-27', output: '27 de março de 2026' },
  // Moeda e número
  { category: 'Moeda', format: 'moeda', syntax: '{{campo||moeda}}', input: '129.25', output: 'R$ 129,25' },
  { category: 'Número', format: 'numero', syntax: '{{campo||numero}}', input: '1490.5', output: '1.490,50' },
  // Documentos
  { category: 'CPF', format: 'cpf', syntax: '{{campo||cpf}}', input: '12345678901', output: '123.456.789-01' },
  { category: 'CNPJ', format: 'cnpj', syntax: '{{campo||cnpj}}', input: '12345678000190', output: '12.345.678/0001-90' },
  { category: 'Telefone', format: 'telefone', syntax: '{{campo||telefone}}', input: '62993252289', output: '(62) 99325-2289' },
  // Texto
  { category: 'Texto', format: 'maiusculo', syntax: '{{campo||maiusculo}}', input: 'joão', output: 'JOÃO' },
  { category: 'Texto', format: 'minusculo', syntax: '{{campo||minusculo}}', input: 'JOÃO', output: 'joão' },
  { category: 'Texto', format: 'capitalizar', syntax: '{{campo||capitalizar}}', input: 'joão silva', output: 'João Silva' },
];

/**
 * Componente de diálogo que exibe tabela com todos os formatos
 * disponíveis para variáveis no sistema
 */
export function VariableFormatHelpDialog({ open, onOpenChange }: VariableFormatHelpDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Formatos de Variáveis</DialogTitle>
          <DialogDescription>
            Use a sintaxe <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">{'{{variavel||formato}}'}</code> para formatar valores automaticamente ao imprimir.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Tipo</TableHead>
                <TableHead>Sintaxe</TableHead>
                <TableHead className="w-[130px]">Entrada</TableHead>
                <TableHead className="w-[160px]">Resultado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {FORMAT_EXAMPLES.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell className="text-xs font-medium">{item.category}</TableCell>
                  <TableCell>
                    <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono break-all">
                      {item.syntax}
                    </code>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{item.input}</TableCell>
                  <TableCell className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{item.output}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Nota explicativa */}
          <div className="mt-4 p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground space-y-1">
            <p><strong>Como usar:</strong> Adicione <code className="bg-muted px-1 rounded font-mono">||formato</code> após o nome da variável, dentro das chaves duplas.</p>
            <p><strong>Exemplo:</strong> <code className="bg-muted px-1 rounded font-mono">{'Vencimento: {{data_vencimento||dd/MM/yyyy}}'}</code></p>
            <p><strong>Sem formato:</strong> <code className="bg-muted px-1 rounded font-mono">{'{{variavel}}'}</code> continua funcionando normalmente, imprimindo o valor bruto.</p>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
